import { useRef, useCallback } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";

const TAP_MOVE_TOLERANCE = 10; // pixels

// iOS-specific utilities to handle viewport offset
const isIOSNative = () =>
  typeof window !== 'undefined' && window.Capacitor?.getPlatform?.() === 'ios';

const getViewportOffsetTop = () => 
  typeof window !== 'undefined' && window.visualViewport 
    ? window.visualViewport.offsetTop 
    : 0;

const getTargetFromPoint = (clientX: number, clientY: number) => {
  const y = clientY + getViewportOffsetTop();
  return document.elementFromPoint(clientX, y) as HTMLElement | null;
};

export function useTouchNavigation() {
  const startXY = useRef<{ x: number; y: number } | null>(null);
  const startTargetRef = useRef<EventTarget | null>(null);
  const startPathRef = useRef<string | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const movedRef = useRef<boolean>(false);
  const navigate = useNavigate();

  const resetRefs = useCallback(() => {
    startXY.current = null;
    startTargetRef.current = null;
    startPathRef.current = null;
    pointerIdRef.current = null;
    movedRef.current = false;
  }, []);

  const makePointerDownHandler = useCallback((path: string) => (e: React.PointerEvent) => {
    // Track only touch/pen to differentiate from mouse
    if (e.pointerType === "touch" || e.pointerType === "pen") {
      startXY.current = { x: e.clientX, y: e.clientY };
      startPathRef.current = path;
      startTargetRef.current = e.currentTarget;
      pointerIdRef.current = e.pointerId;
      movedRef.current = false;
      
      // Capture the pointer to ensure we get all events
      if (typeof (e.currentTarget as any).setPointerCapture === "function") {
        try {
          (e.currentTarget as any).setPointerCapture(e.pointerId);
        } catch (err) {
          console.warn("Failed to capture pointer:", err);
        }
      }
    } else {
      resetRefs();
    }
  }, [resetRefs]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!startXY.current) return;
    
    const dx = Math.abs(e.clientX - startXY.current.x);
    const dy = Math.abs(e.clientY - startXY.current.y);
    
    if (dx > TAP_MOVE_TOLERANCE || dy > TAP_MOVE_TOLERANCE) {
      movedRef.current = true;
    }
  }, []);

  const makePointerUpNavigateHandler = useCallback(
    (path: string) => (e: React.PointerEvent) => {
      if (!e.isPrimary) {
        resetRefs();
        return;
      }

      // Release pointer capture
      if (pointerIdRef.current != null && typeof (e.currentTarget as any).releasePointerCapture === "function") {
        try {
          if ((e.currentTarget as any).hasPointerCapture?.(pointerIdRef.current)) {
            (e.currentTarget as any).releasePointerCapture(pointerIdRef.current);
          }
        } catch (err) {
          console.warn("Failed to release pointer:", err);
        }
      }

      // Verify this is a valid tap:
      // 1. Same path as pointerdown
      const samePath = startPathRef.current === path;
      // 2. Same element as pointerdown
      const sameTarget = startTargetRef.current === e.currentTarget;
      // 3. Pointer didn't move beyond tolerance
      const didNotMove = !movedRef.current;

      if (!samePath || !sameTarget || !didNotMove) {
        // iOS fallback: if on native iOS and pointer didn't move significantly,
        // use elementFromPoint with viewport offset correction
        if (isIOSNative() && !movedRef.current && startXY.current) {
          const el = getTargetFromPoint(startXY.current.x, startXY.current.y);
          const btn = el?.closest('[data-sidebar="menu-button"][data-nav-path]') as HTMLElement | null;
          const dynamicPath = btn?.dataset.navPath;
          if (dynamicPath) {
            e.preventDefault();
            e.stopPropagation();
            navigate(dynamicPath);
            resetRefs();
            return;
          }
        }
        resetRefs();
        return;
      }

      // Valid tap - navigate
      e.preventDefault();
      e.stopPropagation();
      navigate(path);
      resetRefs();
    },
    [navigate, resetRefs]
  );

  const onPointerCancel = useCallback(() => {
    resetRefs();
  }, [resetRefs]);

  const makeKeyHandler = useCallback(
    (path: string) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        navigate(path);
      }
    },
    [navigate]
  );

  return { 
    makePointerDownHandler, 
    onPointerMove, 
    makePointerUpNavigateHandler, 
    onPointerCancel,
    makeKeyHandler 
  };
}
